import React, {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {DayOfWeek} from '@generated/models'
import {
  PractitionerSummary,
  UpsertPractitionerWorkingHourRequest,
  PartnerAdminPractitionerWorkingHourSummary,
} from '@common/models'
import {
  formatDay,
  toTimeString,
  withSeconds,
  isValidTimeHHmm,
  isTimeOrderValid,
} from '@common/utils/dates'

import {SideModal} from '~/components/general/modal/SideModal'
import {Typography} from '~/components/general/typography/Typography'
import {Container} from '~/components/general/container/Container'
import {Input} from '~/components/general/input/Input'
import FilteredSelect from '~/components/general/select/FilteredSelect'
import {Button} from '~/components/general/button/Button'
import {useGetPartnerAdminPractitioners} from '~/api/partnerAdminPractitioners'
import {
  useCreatePractitionerWorkingHour,
  useUpdatePractitionerWorkingHour,
} from '~/api/practitionerWorkingHours'

type StaffWorkingHoursModalProps = {
  isVisible: boolean
  onClose: () => void
  workingHourSummary?: PartnerAdminPractitionerWorkingHourSummary | null
}

function formatDayLabel(day: DayOfWeek): string {
  return formatDay(day)
}

export const StaffWorkingHoursModal: React.FC<StaffWorkingHoursModalProps> = ({
  isVisible,
  onClose,
  workingHourSummary,
}) => {
  const {t} = useTranslation<string>()

  const isEditing = !!workingHourSummary

  const [selectedPractitionerId, setSelectedPractitionerId] = useState<string>(
    workingHourSummary?.practitionerId || '',
  )
  const [selectedDay, setSelectedDay] = useState<DayOfWeek | undefined>(
    workingHourSummary?.dayOfWeek,
  )
  const [startTime, setStartTime] = useState<string>(
    workingHourSummary ? toTimeString(workingHourSummary.startTime) : '',
  )
  const [endTime, setEndTime] = useState<string>(
    workingHourSummary ? toTimeString(workingHourSummary.endTime) : '',
  )

  const {data: practitioners, isLoading: isLoadingPractitioners} =
    useGetPartnerAdminPractitioners(false)

  const practitionerOptions = useMemo(
    () =>
      (practitioners || []).map((p: PractitionerSummary) => ({
        label: `${p.firstName} ${p.secondName}`.trim(),
        value: p.id,
      })),
    [practitioners],
  )

  const dayOptions = useMemo(
    () =>
      Object.values(DayOfWeek).map((value) => ({
        label: formatDayLabel(value),
        value,
      })),
    [],
  )

  const {mutate: createWorkingHour, isPending: isCreating} =
    useCreatePractitionerWorkingHour({
      practitionerId: selectedPractitionerId || '',
    })
  const {mutate: updateWorkingHour, isPending: isUpdating} =
    useUpdatePractitionerWorkingHour({
      practitionerId: workingHourSummary?.practitionerId || '',
      id: workingHourSummary?.id || '',
    })

  const startError = !isValidTimeHHmm(startTime) ? t('Invalid time') : undefined
  const endError = !isValidTimeHHmm(endTime) ? t('Invalid time') : undefined
  const orderingError =
    !startError &&
    !endError &&
    startTime &&
    endTime &&
    !isTimeOrderValid(startTime, endTime)
      ? t('Start time must be before end time')
      : undefined

  const canSave =
    !!selectedPractitionerId &&
    !!selectedDay &&
    !!startTime &&
    !!endTime &&
    !startError &&
    !endError &&
    !orderingError

  useEffect(() => {
    if (!isVisible) return
    if (workingHourSummary) {
      setSelectedPractitionerId(workingHourSummary.practitionerId)
      setSelectedDay(workingHourSummary.dayOfWeek)
      setStartTime(toTimeString(workingHourSummary.startTime))
      setEndTime(toTimeString(workingHourSummary.endTime))
    } else {
      setSelectedPractitionerId('')
      setSelectedDay(undefined)
      setStartTime('')
      setEndTime('')
    }
  }, [isVisible, workingHourSummary])

  const handleSave = () => {
    if (!canSave) return

    const payload: UpsertPractitionerWorkingHourRequest = {
      dayOfWeek: selectedDay,
      startTime: withSeconds(startTime),
      endTime: withSeconds(endTime),
    }

    if (isEditing && workingHourSummary) {
      updateWorkingHour(payload, {
        onSuccess: () => {
          onClose()
        },
      })
    } else {
      createWorkingHour(payload, {
        onSuccess: () => {
          onClose()
        },
      })
    }
  }

  return (
    <SideModal
      isVisible={isVisible}
      onClose={onClose}
      header={
        <Container>
          <Typography size="lg" weight="medium">
            {isEditing ? t('Edit working hour') : t('Add working hour')}
          </Typography>
        </Container>
      }>
      <Container flex direction="column" spacing={3}>
        <Container flex direction="column" spacing={1}>
          <Typography>{t('Practitioner')}</Typography>
          <FilteredSelect
            key={selectedPractitionerId || 'none'}
            width={300}
            placeholder={t('Select a practitioner')}
            options={practitionerOptions}
            initialValue={
              practitionerOptions.find(
                (o) => o.value === selectedPractitionerId,
              )?.label || ''
            }
            onChange={(value) =>
              setSelectedPractitionerId((value as string) || '')
            }
            disabled={isEditing || isLoadingPractitioners}
          />
        </Container>

        <Container flex direction="column" spacing={1}>
          <Typography>{t('Day of week')}</Typography>
          <FilteredSelect
            key={selectedDay ?? 'none'}
            width={300}
            placeholder={t('Select a day')}
            options={dayOptions}
            initialValue={
              selectedDay
                ? dayOptions.find((d) => d.value === selectedDay)?.label
                : ''
            }
            onChange={(value) => setSelectedDay(value as DayOfWeek | undefined)}
          />
        </Container>

        <Container flex spacing={3}>
          <Container flex direction="column" spacing={1}>
            <Typography>{t('Start time')}</Typography>
            <Input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
              errorMessage={startError || orderingError}
            />
          </Container>
          <Container flex direction="column" spacing={1}>
            <Typography>{t('End time')}</Typography>
            <Input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
              errorMessage={endError || orderingError}
            />
          </Container>
        </Container>

        <Container flex justify="flex-end" spacing={2}>
          <Button onClick={onClose}>{t('Cancel')}</Button>
          <Button
            color="primary"
            onClick={handleSave}
            disabled={!canSave || isCreating || isUpdating}>
            {t('Save')}
          </Button>
        </Container>
      </Container>
    </SideModal>
  )
}

export default StaffWorkingHoursModal
